import { PDFDocument } from "pdf-lib";
import { downloadBlob } from "operations/operations";

export const renderEmbeddedUpcLabel = ({
  qrCodeBase64,
  upc,
  product,
  packedOn,
  expiresOn,
}) =>
  new Promise((resolve, reject) => {
    if (!qrCodeBase64) {
      reject(new Error("qrCodeBase64 is required"));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `data:image/png;base64,${qrCodeBase64}`;

    img.onload = () => {
      try {
        const qrSize = 600;
        const cellPadding = 28;
        const fontName = "Arial";
        const fontSize = 50;
        const productFontSize = 56;
        const lineHeight = fontSize * 1.7;
        const borderWidth = 10;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // --- Top bar: product name ---
        const productText = product ? String(product) : "";
        const topBarPaddingY = 24;
        const cellWidth = qrSize + cellPadding * 2;
        const totalWidth = cellWidth * 2;

        // Wrap product name across the full width.
        const productMaxWidth = totalWidth - cellPadding * 2;
        ctx.font = `bold ${productFontSize}px ${fontName}`;
        const productLines = [];
        if (productText) {
          const words = productText.split(/\s+/).filter(Boolean);
          let current = "";
          words.forEach((word) => {
            const candidate = current ? `${current} ${word}` : word;
            if (ctx.measureText(candidate).width <= productMaxWidth) {
              current = candidate;
            } else {
              if (current) productLines.push(current);
              current = word;
            }
          });
          if (current) productLines.push(current);
        }
        const productLineHeight = productFontSize * 1.4;
        const topBarHeight = productLines.length
          ? topBarPaddingY * 2 + productLines.length * productLineHeight
          : 0;

        // --- Info lines (right cell, no product) ---
        const rawLines = [
          ["UPC", upc],
          ["Packed on", packedOn],
          ["Expires on", expiresOn],
        ]
          .filter(([, v]) => v !== undefined && v !== null && String(v).length > 0)
          .map(([k, v]) => ({ key: `${k}:`, value: String(v) }));

        const leftCellWidth = cellWidth;
        const rightCellWidth = rawLines.length ? cellWidth : 0;
        const textMaxWidth = rightCellWidth - cellPadding * 2;

        const wrapPair = ({ key, value }) => {
          ctx.font = `bold ${fontSize}px ${fontName}`;
          const keyWidth = ctx.measureText(`${key} `).width;
          ctx.font = `${fontSize}px ${fontName}`;

          const words = String(value).split(/\s+/).filter(Boolean);
          const segments = [];
          let currentText = "";
          let availableWidth = textMaxWidth - keyWidth;
          let isFirst = true;

          words.forEach((word) => {
            const candidate = currentText ? `${currentText} ${word}` : word;
            if (ctx.measureText(candidate).width <= availableWidth) {
              currentText = candidate;
            } else {
              segments.push({ text: currentText, isFirst });
              isFirst = false;
              currentText = word;
              availableWidth = textMaxWidth;
            }
          });
          segments.push({ text: currentText, isFirst });
          return segments.map((seg) => ({ key, value: seg.text, isFirst: seg.isFirst }));
        };

        const lines = rawLines.flatMap(wrapPair);
        const textBlockHeight = lines.length * lineHeight;

        const bodyHeight = Math.max(qrSize, textBlockHeight) + cellPadding * 2;
        const canvasWidth = Math.ceil(totalWidth);
        const canvasHeight = Math.ceil(topBarHeight + bodyHeight);

        // Render at 2x for sharper output.
        const scale = 2;
        canvas.width = canvasWidth * scale;
        canvas.height = canvasHeight * scale;
        ctx.scale(scale, scale);

        // Background.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // --- Draw top bar ---
        if (productLines.length) {
          ctx.fillStyle = "#000000";
          ctx.font = `bold ${productFontSize}px ${fontName}`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const totalTextHeight = productLines.length * productLineHeight;
          let py = (topBarHeight - totalTextHeight) / 2 + productLineHeight / 2 + 8;
          productLines.forEach((line) => {
            ctx.fillText(line, canvasWidth / 2, py);
            py += productLineHeight;
          });
        }

        // --- Draw body ---
        const bodyTop = topBarHeight;

        // QR (left cell, centered vertically within body).
        ctx.imageSmoothingEnabled = false;
        const qrX = (leftCellWidth - qrSize) / 2;
        const qrY = bodyTop + (bodyHeight - qrSize) / 2;
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
        ctx.imageSmoothingEnabled = true;

        // Text block (right cell, vertically centered within body).
        if (lines.length) {
          const textX = leftCellWidth + cellPadding;
          const textY = bodyTop + (bodyHeight - textBlockHeight) / 2;

          ctx.fillStyle = "#000000";
          ctx.textAlign = "left";
          ctx.textBaseline = "top";

          let y = textY;
          lines.forEach((line) => {
            if (line.isFirst) {
              ctx.font = `bold ${fontSize}px ${fontName}`;
              const keyWidth = ctx.measureText(`${line.key} `).width;
              ctx.fillText(line.key, textX, y);
              ctx.font = `${fontSize}px ${fontName}`;
              ctx.fillText(line.value, textX + keyWidth, y);
            } else {
              ctx.font = `${fontSize}px ${fontName}`;
              ctx.fillText(line.value, textX, y);
            }
            y += lineHeight;
          });
        }

        // Outer border.
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = borderWidth;
        ctx.strokeRect(
          borderWidth,
          borderWidth,
          canvasWidth - borderWidth * 2,
          canvasHeight - borderWidth * 2
        );

        // Horizontal divider between top bar and body.
        if (productLines.length) {
          ctx.beginPath();
          ctx.moveTo(borderWidth, topBarHeight);
          ctx.lineTo(canvasWidth - borderWidth, topBarHeight);
          ctx.stroke();
        }

        // Vertical divider between QR cell and info cell.
        if (lines.length) {
          ctx.beginPath();
          ctx.moveTo(leftCellWidth, topBarHeight > 0 ? topBarHeight : borderWidth);
          ctx.lineTo(leftCellWidth, canvasHeight - borderWidth);
          ctx.stroke();
        }

        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: canvasWidth,
          height: canvasHeight,
        });
      } catch (e) {
        reject(e);
      }
    };

    img.onerror = () => reject(new Error("Failed to load QR code image"));
  });

export const downloadEmbeddedUpcLabelPdf = async ({
  qrCodeBase64,
  upc,
  product,
  packedOn,
  expiresOn,
  fileName = "embedded_upc_label",
}) => {
  const { dataUrl, width, height } = await renderEmbeddedUpcLabel({
    qrCodeBase64,
    upc,
    product,
    packedOn,
    expiresOn,
  });

  const pngBytes = await fetch(dataUrl).then((r) => r.arrayBuffer());

  const pdfDoc = await PDFDocument.create();
  const pngImage = await pdfDoc.embedPng(pngBytes);

  // Page sized to label (1px = 1pt) so the label fills the page.
  const page = pdfDoc.addPage([width, height]);
  page.drawImage(pngImage, { x: 0, y: 0, width, height });

  const pdfBytes = await pdfDoc.save();
  downloadBlob(pdfBytes, `${fileName}.pdf`, "application/pdf");
};

export const downloadEmbeddedUpcLabelPng = async ({
  qrCodeBase64,
  upc,
  product,
  packedOn,
  expiresOn,
  fileName = "embedded_upc_label",
}) => {
  const { dataUrl } = await renderEmbeddedUpcLabel({
    qrCodeBase64,
    upc,
    product,
    packedOn,
    expiresOn,
  });

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `${fileName}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
